import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MayorButton } from "../models/apiTypes";
import { mayorGreetingMayorGreetingPost, mayorReplyMayorReplyPost } from "../client";
import { handleApiError } from "../utils/net/apiError";
import { useTypewriter } from "../hooks/useTypewriter";
import UserAvatar from "../components/UserAvatar";
import { cn } from "../ui/cn";
import posthog from "posthog-js";


const MAYOR_FALLBACK_IMAGE = "/game-assets/nscene-characters/character-elder.png";

interface TalkToMayorScreenProps {
  onBack: () => void;
  mayorPhotoUrl: string | null;
  mayorVipUntil: string | null;
}

interface ChatMessage {
  role: "mayor" | "user";
  text: string;
  buttons?: MayorButton[];
}

export default function TalkToMayorScreen({ onBack, mayorPhotoUrl, mayorVipUntil }: TalkToMayorScreenProps) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentMayorText, setCurrentMayorText] = useState<string | undefined>(undefined);
  const [currentButtons, setCurrentButtons] = useState<MayorButton[]>([]);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customText, setCustomText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const displayedText = useTypewriter(currentMayorText);
  const hasGreetedRef = useRef(false);

  const mayorAvatarData = {
    photo_url: mayorPhotoUrl ?? MAYOR_FALLBACK_IMAGE,
    first_name: "Mayor",
    vip_until: mayorVipUntil,
  };

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, displayedText, currentButtons, showCustomInput]);

  // Focus input when showing custom input
  useEffect(() => {
    if (showCustomInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCustomInput]);

  // Close on Escape
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onBack();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onBack]);

  // Initial greeting
  useEffect(() => {
    if (hasGreetedRef.current) return;
    hasGreetedRef.current = true;

    // Track conversation start
    try { posthog.capture("mayor_conversation_started"); } catch { /* never block game */ }

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const resp = await (async () => {
          const { data } = await mayorGreetingMayorGreetingPost({ throwOnError: true });
          return data;
        })();
        setCurrentMayorText(resp.text);
        setCurrentButtons(resp.buttons as MayorButton[]);
        setMessages([{ role: "mayor", text: resp.text, buttons: resp.buttons as MayorButton[] }]);
        // Track first mayor message
        try { posthog.capture("mayor_first_response"); } catch { /* never block game */ }
      } catch {
        setError(t("Failed to connect to the Mayor. Try again later."));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const handleSendMessage = useCallback(async (text: string, isButton: boolean) => {
    // Track user message
    try { posthog.capture("mayor_user_message", { type: isButton ? "button" : "custom" }); } catch { /* never block game */ }

    setCurrentMayorText(undefined);
    // Keep buttons visible during loading to prevent layout jump
    setMessages(prev => [...prev, { role: "user", text }]);
    setLoading(true);
    setError(null);

    try {
      const body = isButton ? { button_label: text } : { custom_text: text };
      const { data: resp } = await mayorReplyMayorReplyPost({ body, throwOnError: true });
      setCurrentMayorText(resp.text);
      setCurrentButtons(resp.buttons as MayorButton[]);
      setMessages(prev => [...prev, { role: "mayor", text: resp.text, buttons: resp.buttons as MayorButton[] }]);
      // Track mayor response
      try { posthog.capture("mayor_response_received"); } catch { /* never block game */ }
    } catch (err) {
      handleApiError(err, { logLabel: 'sendMayorReply' });
      setError(t("The Mayor seems busy. Try again."));
      // Restore buttons on error so user can retry
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleButtonPress = useCallback(async (btn: MayorButton) => {
    if (btn.id === "custom") {
      setShowCustomInput(true);
      return;
    }
    await handleSendMessage(btn.label, true);
  }, [handleSendMessage]);

  const handleCustomSubmit = useCallback(async () => {
    const text = customText.trim();
    if (!text) return;

    setShowCustomInput(false);
    setCustomText("");
    await handleSendMessage(text, false);
  }, [customText, handleSendMessage]);

  // Show typewriter for the last mayor message only
  const isTyping = currentMayorText !== undefined && displayedText !== currentMayorText;

  return (
    <div
      className="relative flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-[linear-gradient(180deg,var(--color-election-story-frame-accent)_0%,var(--color-talk-to-mayor-screen-surface)_50%,var(--color-talk-to-mayor-screen-surface-2)_100%)] text-white [animation:mayor-chat-fadein_0.4s_ease-out]"
    >
      <style>{`
        @keyframes mayor-chat-fadein {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes mayor-avatar-pulse {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 8px var(--color-brand-alpha-30)); }
          50% { transform: scale(1.04); filter: drop-shadow(0 0 20px var(--color-brand-alpha-60)); }
        }
        @keyframes mayor-bubble-slidein {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes mayor-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes typing-dot {
          0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-4px); }
        }
        @keyframes mayor-buttons-fadein {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {/* Header — solid bg; only × (Telegram already provides native Back) */}
      <div className="relative z-[1] flex shrink-0 items-center gap-lg border-b border-solid border-white-alpha-10 bg-election-story-frame-accent px-xl py-lg">
        <UserAvatar
          user={mayorAvatarData}
          size={40}
        />
        <div>
          <div className="[font-family:'Rubik_One',sans-serif] text-[16px]">
            {t("The Mayor")}
          </div>
          <div className="[font-family:'Lato',sans-serif] text-[12px] opacity-60">
            {loading || isTyping ? t("typing...") : t("Online")}
          </div>
        </div>
        <button
          onClick={onBack}
          aria-label={t("Close")}
          className="ml-auto cursor-pointer border-none bg-transparent px-[10px] py-xs text-[26px] leading-[26px] text-white"
        >
          ×
        </button>
      </div>

      {/* Chat area */}
      <div
        ref={scrollRef}
        className="flex min-h-0 flex-1 flex-col gap-lg overflow-y-auto p-xl"
      >
        {/* Mayor character portrait (shown at start) */}
        {messages.length <= 1 && (
          <div
            className={cn(
              "pb-md text-center [&>div]:mx-auto",
              loading && "[animation:mayor-avatar-pulse_2s_ease-in-out_infinite]",
            )}
          >
            <UserAvatar
              user={mayorAvatarData}
              size={120}
            />
          </div>
        )}

        {/* Typing indicator shown immediately on open, before first message arrives */}
        {loading && messages.length === 0 && (
          <div
            className="relative flex flex-col gap-[10px] self-start overflow-hidden rounded-[18px_18px_18px_4px] bg-[linear-gradient(135deg,var(--color-talk-to-mayor-screen-surface-3)_0%,var(--color-talk-to-mayor-screen-surface-4)_100%)] bg-[length:100%_100%] px-2xl py-[14px] [animation:mayor-bubble-slidein_0.5s_ease-out]"
          >
            {/* Shimmer overlay */}
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,var(--color-brand-alpha-8)_50%,transparent_100%)] bg-[length:200%_100%] [animation:mayor-shimmer_2.5s_linear_infinite]" />
            <span className="relative [font-family:'Lato',sans-serif] text-[13px] text-white-alpha-60">
              {t("The mayor is writing a message...")}
            </span>
            <div className="relative flex items-center gap-sm">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-[10px] w-[10px] rounded-full bg-brand-alpha-80"
                  style={{
                    animation: `typing-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, i) => {
          const isMayor = msg.role === "mayor";
          const isLast = i === messages.length - 1;
          const text = isMayor && isLast && currentMayorText ? displayedText : msg.text;

          return (
            <div
              key={i}
              className={cn(
                "max-w-[85%] [animation:mayor-bubble-slidein_0.3s_ease-out]",
                isMayor ? "self-start" : "self-end",
              )}
            >
              <div
                className={cn(
                  "whitespace-pre-wrap px-[14px] py-[10px] [font-family:'Lato',sans-serif] text-[14px] leading-[20px] shadow-[0_2px_8px_var(--color-black-alpha-20)]",
                  isMayor
                    ? "rounded-[16px_16px_16px_4px] bg-[linear-gradient(135deg,var(--color-talk-to-mayor-screen-surface-3)_0%,var(--color-talk-to-mayor-screen-surface-4)_100%)]"
                    : "rounded-[16px_16px_4px_16px] bg-[linear-gradient(135deg,var(--color-brand)_0%,var(--color-talk-to-mayor-screen-text)_100%)]",
                )}
              >
                {text}
              </div>
            </div>
          );
        })}

        {/* Typing indicator while waiting for reply */}
        {loading && messages.length > 0 && !currentMayorText && (
          <div
            className="relative flex items-center gap-sm self-start overflow-hidden rounded-[18px_18px_18px_4px] bg-[linear-gradient(135deg,var(--color-talk-to-mayor-screen-surface-3)_0%,var(--color-talk-to-mayor-screen-surface-4)_100%)] px-2xl py-[14px] [animation:mayor-bubble-slidein_0.3s_ease-out]"
          >
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,var(--color-brand-alpha-8)_50%,transparent_100%)] bg-[length:200%_100%] [animation:mayor-shimmer_2.5s_linear_infinite]" />
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="relative h-[10px] w-[10px] rounded-full bg-brand-alpha-80"
                style={{
                  animation: `typing-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
                }}
              />
            ))}
          </div>
        )}

        {error && (
          <div
            className="rounded-sm bg-danger-muted-alpha-20 px-xl py-md text-center [font-family:'Lato',sans-serif] text-[13px]"
          >
            {error}
          </div>
        )}
      </div>

      {/* Bottom: reply options — always pinned under the chat column */}
      <div className="flex shrink-0 flex-col gap-md border-t border-solid border-white-alpha-10 bg-election-story-frame-accent px-xl pt-lg pb-[max(12px,env(safe-area-inset-bottom))]">
        {showCustomInput && (
          <div className="flex gap-md">
            <input
              ref={inputRef}
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCustomSubmit()}
              placeholder={t("Type your message...")}
              maxLength={500}
              className="flex-1 rounded-xl border border-solid border-brand-alpha-50 bg-white-alpha-8 px-[14px] py-[10px] [font-family:'Lato',sans-serif] text-[14px] text-white outline-none"
            />
            <button
              onClick={handleCustomSubmit}
              disabled={!customText.trim()}
              className="cursor-pointer rounded-xl border-none bg-brand px-[18px] py-[10px] [font-family:'Lato',sans-serif] text-[14px] font-bold text-white disabled:cursor-not-allowed disabled:bg-text-dim"
            >
              →
            </button>
          </div>
        )}

        {!showCustomInput && currentButtons.length > 0 && !isTyping && (
          <div className="flex flex-col gap-md [animation:mayor-buttons-fadein_0.4s_ease-out]">
            {currentButtons.map((btn) => (
              <button
                key={btn.id}
                onClick={() => handleButtonPress(btn)}
                disabled={loading}
                className={cn(
                  "w-full cursor-pointer rounded-md px-xl py-lg text-left [font-family:'Lato',sans-serif] text-[14px] font-medium text-white [transition:background_0.2s] disabled:cursor-not-allowed disabled:opacity-50",
                  btn.id === "custom"
                    ? "border border-dashed border-brand-alpha-60 bg-white-alpha-4"
                    : "border border-solid border-brand-alpha-40 bg-brand-alpha-15",
                )}
              >
                {btn.id === "custom" ? `✍️ ${btn.label}` : `▸ ${btn.label}`}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
